import { CanvasTexture, LinearSRGBColorSpace, RepeatWrapping, SRGBColorSpace } from 'three';

/**
 * 주행 노면(아스팔트) 텍스처를 캔버스로 만듭니다.
 *
 * 왜 이미지를 받아 오지 않는가:
 *   외부 텍스처를 쓰면 라이선스 표기, `assets/` → `public/` 변환 스크립트, 배포 용량
 *   (색상·노멀·러프니스 세 장이면 WebP로도 수백 KB)이 함께 붙습니다. 이 노면에
 *   필요한 것은 사진의 디테일이 아니라 **속도감을 만드는 알갱이**입니다. 단색 바닥에서는
 *   흘러가는 것이 없어서 달리는지 알 수 없는데, 그건 절차적 노이즈로 충분히 해결됩니다.
 *   같은 이유로 이 저장소는 이미 화면 로고와 접지 그림자를 캔버스로 만듭니다.
 *
 * 세 장을 만듭니다.
 *   - 색상(albedo): 아스팔트 회색 + 골재 알갱이
 *   - 노멀: 같은 높이장에서 뽑은 요철. 이게 있어야 HDRI 조명 아래에서 노면처럼 읽힙니다
 *     (색만 얼룩지게 하면 무늬를 인쇄한 평면으로 보입니다)
 *   - 러프니스: 반사 세기를 흩어서 하이라이트가 한 덩어리로 뭉치지 않게
 */

/** 한 장의 픽셀 크기. 2의 거듭제곱이라 밉맵이 깔끔하게 만들어집니다. */
const TEXTURE_SIZE = 512;

/**
 * 텍스처 한 장이 덮는 실제 거리 (m). 5cm/텍셀입니다.
 *
 * 추격 카메라가 차에서 8m 거리라 노면이 화면 가까이 옵니다. 타일이 이보다 커지면
 * 알갱이가 뭉개져서 속도감이 사라지고, 작아지면 반복이 눈에 띕니다.
 */
export const ASPHALT_TILE_METRES = 2.5;

/** 아스팔트 기본 색. 완전 무채색보다 살짝 푸른 쪽이 실제 노면에 가깝습니다. */
const BASE_RGB = [64, 67, 72];

/**
 * 세 성분을 겹칩니다. 처음에는 매끄러운 노이즈만 썼는데 사포처럼 균일해서, 실제 노면을
 * 노면으로 보이게 하는 것이 무엇인지 다시 봤습니다.
 *
 *   - 얼룩(mottle): 40cm~1m 크기. 포장 이음새와 보수 흔적, 닳아서 반들해진 자리.
 *   - 골재(aggregate): 1~2cm 크기의 **덩어리**. 매끄러운 노이즈를 임계 처리해서
 *     알갱이와 그 사이 바인더로 갈라 놓습니다. 이게 없으면 밝기만 흔들리는 회색이 됩니다.
 *   - 균열(crack): 얇고 어두운 선. 몇 개만 있어도 "포장면"이라는 신호가 강해집니다.
 *
 * 타일 한 장이 2.5m를 512텍셀로 덮으므로 1텍셀이 약 4.9mm입니다. 골재 격자를 170으로
 * 두면 한 칸이 3텍셀 = 약 1.5cm로, 실제 골재 크기와 맞습니다.
 */
const MOTTLE_CONTRAST = 0.18;
const AGGREGATE_CONTRAST = 0.38;
/** 격자 128 → 한 칸이 4텍셀 ≈ 2cm. 실제 골재 크기입니다. */
const AGGREGATE_GRID = 128;
/** 이 구간에서 골재와 바인더가 갈립니다. 좁히면 알갱이 경계가 또렷해집니다. */
const AGGREGATE_EDGE = [0.42, 0.72];

/**
 * 균열은 **저주파** 노이즈의 능선에서 뽑습니다.
 *
 * 처음에는 fbm(5옥타브)의 능선을 썼는데, 고주파 성분이 능선을 잘게 끊어서 균열이 아니라
 * 검은 얼룩이 흩뿌려졌습니다. 매끄러운 한 옥타브만 쓰면 능선이 길고 완만한 곡선이 되어
 * 실제로 갈라진 금처럼 보입니다.
 */
const CRACK_GRID = 9;
/** 능선이 이 구간을 넘는 곳만 균열입니다. 좁고 높게 두면 선이 얇아집니다. */
const CRACK_THRESHOLD = [0.94, 1];
/** 균열이 나타날 구역을 다시 걸러서 노면 전체가 갈라져 보이지 않게 합니다. */
const CRACK_REGION_GRID = 5;
const CRACK_REGION_EDGE = [0.52, 0.78];
const CRACK_DARKENING = 0.34;

const ROUGHNESS_BASE = 0.74;
/** 골재는 거칠고, 닳아 반들해진 얼룩은 매끄럽게. */
const ROUGHNESS_AGGREGATE = 0.16;
const ROUGHNESS_MOTTLE = 0.12;

/** 노멀 강도. 요철의 기울기를 얼마나 세게 볼지. */
const NORMAL_STRENGTH = 2.2;

/**
 * 정수 격자 해시. 0~1을 돌려줍니다.
 *
 * Math.random을 쓰지 않는 이유: 새로 고칠 때마다 노면 무늬가 달라지면 같은 화면을
 * 다시 볼 수 없고, 테스트로 값을 확인할 수도 없습니다.
 */
function hash(x: number, y: number, seed: number): number {
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

const smoothstep = (t: number): number => t * t * (3 - 2 * t);

/**
 * 격자 하나. 무작위 값을 미리 채워 둔 표입니다.
 *
 * 왜 픽셀마다 해시를 다시 부르지 않는가:
 *   처음에는 샘플할 때마다 네 꼭짓점을 해시로 계산했습니다. 픽셀 하나에 노이즈를 여덟
 *   번 정도 뽑으니 512²에서 해시가 800만 번 넘게 불렸고, 텍스처 생성이 **2.5초**나
 *   걸렸습니다. 주행 모드로 들어갈 때 그만큼 화면이 멈춥니다.
 *   격자는 크기가 최대 256×256(6.5만 칸)뿐이라 미리 채워 두면 픽셀 루프에서는 배열
 *   읽기만 남습니다.
 */
interface Lattice {
  grid: number;
  values: Float32Array;
}

function createLattice(grid: number, seed: number): Lattice {
  const values = new Float32Array(grid * grid);
  for (let y = 0; y < grid; y += 1) {
    for (let x = 0; x < grid; x += 1) {
      values[y * grid + x] = hash(x, y, seed);
    }
  }
  return { grid, values };
}

/**
 * 주기적 값 노이즈. 격자 좌표를 grid로 감싸므로 상하좌우로 이음선 없이 반복됩니다.
 *
 * 이음선이 남으면 넓은 노면에 격자무늬가 생깁니다.
 *
 * @param u 0~1 UV 좌표
 */
function sampleLattice(lattice: Lattice, u: number, v: number): number {
  const { grid, values } = lattice;
  const gx = u * grid;
  const gy = v * grid;
  const x0 = Math.floor(gx);
  const y0 = Math.floor(gy);
  const fx = smoothstep(gx - x0);
  const fy = smoothstep(gy - y0);
  // 이미 0 이상이라 나머지 연산만으로 감쌉니다.
  const wx0 = x0 % grid;
  const wy0 = y0 % grid;
  const wx1 = (x0 + 1) % grid;
  const wy1 = (y0 + 1) % grid;

  const row0 = wy0 * grid;
  const row1 = wy1 * grid;
  const a = values[row0 + wx0];
  const b = values[row0 + wx1];
  const c = values[row1 + wx0];
  const d = values[row1 + wx1];
  return a * (1 - fx) * (1 - fy) + b * fx * (1 - fy) + c * (1 - fx) * fy + d * fx * fy;
}

/**
 * 얼룩용 옥타브 격자.
 *
 * 격자 크기가 텍스처 크기를 나누어야 반복이 유지됩니다. 128까지 있던 최고 옥타브는
 * 뺐습니다 — 그 주파수는 골재가 이미 담당하고, 옥타브 하나가 곧 생성 시간입니다.
 */
const OCTAVE_GRIDS = [8, 16, 32, 64];

interface SurfaceLattices {
  mottle: Lattice[];
  aggregate: Lattice;
  crackRidge: Lattice;
  crackRegion: Lattice;
}

function createSurfaceLattices(): SurfaceLattices {
  return {
    mottle: OCTAVE_GRIDS.map((grid, index) => createLattice(grid, 7 + index * 101)),
    aggregate: createLattice(AGGREGATE_GRID, 991),
    crackRidge: createLattice(CRACK_GRID, 313),
    crackRegion: createLattice(CRACK_REGION_GRID, 77),
  };
}

/** 옥타브를 겹칩니다. 진폭은 격자가 커질수록 절반씩 줄어듭니다. */
function fractalNoise(lattices: Lattice[], u: number, v: number): number {
  let sum = 0;
  let amplitude = 0.5;
  let total = 0;
  for (let i = 0; i < lattices.length; i += 1) {
    sum += sampleLattice(lattices[i], u, v) * amplitude;
    total += amplitude;
    amplitude *= 0.5;
  }
  return sum / total;
}

/** 0~1 구간 밖은 잘라내고 안에서는 부드럽게 올라가는 계단. */
function step(edge0: number, edge1: number, value: number): number {
  if (value <= edge0) return 0;
  if (value >= edge1) return 1;
  return smoothstep((value - edge0) / (edge1 - edge0));
}

/**
 * 노면을 이루는 세 성분. 픽셀마다 한 번만 계산해서 색·노멀·러프니스가 함께 씁니다.
 */
interface SurfaceSample {
  /** 40cm~1m 얼룩. 0~1. */
  mottle: number;
  /** 골재 알갱이. 0(바인더)~1(알갱이). */
  aggregate: number;
  /** 균열. 0(없음)~1(균열 중심). */
  crack: number;
}

function sampleSurface(lattices: SurfaceLattices, u: number, v: number): SurfaceSample {
  const mottle = fractalNoise(lattices.mottle, u, v);
  // 매끄러운 노이즈를 임계 처리해서 알갱이와 바인더로 갈라 놓습니다.
  const aggregate = step(AGGREGATE_EDGE[0], AGGREGATE_EDGE[1], sampleLattice(lattices.aggregate, u, v));
  /*
   * 능선 노이즈(1 − |2n − 1|)는 노이즈의 중간값 자리에서 1이 되어 선처럼 이어집니다.
   * 거기서 가장 높은 띠만 남기면 얇고 구불구불한 금이 됩니다.
   */
  const ridge = 1 - Math.abs(2 * sampleLattice(lattices.crackRidge, u, v) - 1);
  const region = step(
    CRACK_REGION_EDGE[0],
    CRACK_REGION_EDGE[1],
    sampleLattice(lattices.crackRegion, u, v)
  );
  const crack = step(CRACK_THRESHOLD[0], CRACK_THRESHOLD[1], ridge) * region;
  return { mottle, aggregate, crack };
}

export interface AsphaltTextures {
  color: CanvasTexture;
  normal: CanvasTexture;
  roughness: CanvasTexture;
}

/** 세 장의 RGBA 픽셀. 채널 순서와 크기는 캔버스 ImageData와 같습니다. */
export interface AsphaltPixels {
  size: number;
  color: Uint8ClampedArray;
  normal: Uint8ClampedArray;
  roughness: Uint8ClampedArray;
}

/**
 * 픽셀을 채웁니다. DOM에 의존하지 않는 순수 함수입니다.
 *
 * 캔버스에서 떼어 낸 이유는 확인 때문입니다. jsdom에는 2D 컨텍스트가 없어서 캔버스를
 * 거치면 테스트에서 결과를 볼 수 없습니다. 여기서 나온 배열은 그대로 검사할 수 있고
 * PNG로 저장해 눈으로 볼 수도 있습니다 — 노면이 그럴싸한지는 수치로만 판단할 수 없습니다.
 */
export function fillAsphaltPixels(size: number = TEXTURE_SIZE): AsphaltPixels {
  const color = new Uint8ClampedArray(size * size * 4);
  const normal = new Uint8ClampedArray(size * size * 4);
  const roughness = new Uint8ClampedArray(size * size * 4);

  /*
   * 성분을 먼저 전부 채웁니다. 노멀이 이웃 픽셀의 높이를 봐야 하고, 픽셀마다 노이즈를
   * 다시 계산하면 같은 일을 세 번 하게 됩니다.
   */
  const lattices = createSurfaceLattices();
  const height = new Float32Array(size * size);
  const mottleField = new Float32Array(size * size);
  const aggregateField = new Float32Array(size * size);
  const crackField = new Float32Array(size * size);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const index = y * size + x;
      const { mottle, aggregate, crack } = sampleSurface(lattices, x / size, y / size);
      mottleField[index] = mottle;
      aggregateField[index] = aggregate;
      crackField[index] = crack;
      // 골재는 솟아 있고 균열은 파여 있습니다. 얼룩은 완만한 기복입니다.
      height[index] = aggregate * 0.6 + mottle * 0.25 - crack * 0.8;
    }
  }

  // 반복 텍스처라 경계에서 반대편을 읽습니다. 그래야 이음선에 선이 생기지 않습니다.
  const sample = (x: number, y: number): number => {
    const wx = ((x % size) + size) % size;
    const wy = ((y % size) + size) % size;
    return height[wy * size + wx];
  };

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const index = y * size + x;
      const offset = index * 4;
      const mottle = mottleField[index];
      const aggregate = aggregateField[index];
      const crack = crackField[index];

      const brightness =
        (1 + (mottle - 0.5) * MOTTLE_CONTRAST + (aggregate - 0.5) * AGGREGATE_CONTRAST) *
        (1 - crack * CRACK_DARKENING);
      color[offset] = Math.round(BASE_RGB[0] * brightness);
      color[offset + 1] = Math.round(BASE_RGB[1] * brightness);
      color[offset + 2] = Math.round(BASE_RGB[2] * brightness);
      color[offset + 3] = 255;

      // 중앙 차분으로 기울기를 구해 탄젠트 공간 노멀로 바꿉니다.
      const dx = (sample(x + 1, y) - sample(x - 1, y)) * NORMAL_STRENGTH;
      const dy = (sample(x, y + 1) - sample(x, y - 1)) * NORMAL_STRENGTH;
      const length = Math.hypot(dx, dy, 1);
      normal[offset] = Math.round(((-dx / length) * 0.5 + 0.5) * 255);
      normal[offset + 1] = Math.round(((-dy / length) * 0.5 + 0.5) * 255);
      normal[offset + 2] = Math.round((1 / length) * 0.5 * 255 + 127.5);
      normal[offset + 3] = 255;

      /*
       * 골재가 드러난 곳은 거칠고, 닳아 반들해진 얼룩은 매끄럽게.
       * 러프니스는 G 채널만 읽지만 세 채널을 같이 채워 두면 다른 채널로 바꿔 쓸 때
       * 결과가 달라지지 않습니다.
       */
      const rough =
        ROUGHNESS_BASE + (aggregate - 0.5) * ROUGHNESS_AGGREGATE - (mottle - 0.5) * ROUGHNESS_MOTTLE;
      const roughByte = Math.round(rough * 255);
      roughness[offset] = roughByte;
      roughness[offset + 1] = roughByte;
      roughness[offset + 2] = roughByte;
      roughness[offset + 3] = 255;
    }
  }

  return { size, color, normal, roughness };
}

function createCanvas(): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } | null {
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const context = canvas.getContext('2d');
  if (context === null) {
    return null;
  }
  return { canvas, context };
}

function toTexture(canvas: HTMLCanvasElement, srgb: boolean): CanvasTexture {
  const texture = new CanvasTexture(canvas);
  /*
   * 색상 맵만 sRGB입니다. 노멀과 러프니스는 색이 아니라 수치라서 sRGB로 읽으면
   * 감마가 한 번 더 적용되어 요철과 광택이 어긋납니다.
   */
  texture.colorSpace = srgb ? SRGBColorSpace : LinearSRGBColorSpace;
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  return texture;
}

/**
 * 아스팔트 텍스처 세 장.
 *
 * 픽셀을 한 번만 훑으면서 세 장을 함께 채웁니다. 높이장을 세 번 계산하면 512² × 5옥타브를
 * 세 번 돌게 되는데, 노멀은 그 높이장의 기울기라서 어차피 같은 값이 필요합니다.
 *
 * @returns 2D 컨텍스트가 없는 환경(jsdom)에서는 null.
 */
/**
 * 픽셀은 한 번만 만듭니다.
 *
 * 주행 모드를 드나들 때마다 다시 계산하면 그때마다 프레임이 멈춥니다. 결과가 항상
 * 같으므로(해시가 결정적입니다) 재사용해도 화면이 달라지지 않습니다.
 *
 * 텍스처가 아니라 **픽셀 배열**을 캐시하는 이유: 텍스처는 GPU 자원이라 컴포넌트가
 * 언마운트될 때 해제해야 합니다. 텍스처를 공유하면 한쪽이 해제한 것을 다른 쪽이
 * 쓰게 됩니다. 픽셀 배열은 그냥 메모리(512²×4×3 = 3MB)라 들고 있어도 됩니다.
 */
let cachedPixels: AsphaltPixels | null = null;

export function createAsphaltTextures(): AsphaltTextures | null {
  const colorTarget = createCanvas();
  const normalTarget = createCanvas();
  const roughnessTarget = createCanvas();
  if (colorTarget === null || normalTarget === null || roughnessTarget === null) {
    return null;
  }

  if (cachedPixels === null) {
    cachedPixels = fillAsphaltPixels(TEXTURE_SIZE);
  }
  const pixels = cachedPixels;
  const put = (
    target: { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D },
    data: Uint8ClampedArray
  ) => {
    const image = target.context.createImageData(pixels.size, pixels.size);
    image.data.set(data);
    target.context.putImageData(image, 0, 0);
  };

  put(colorTarget, pixels.color);
  put(normalTarget, pixels.normal);
  put(roughnessTarget, pixels.roughness);

  return {
    color: toTexture(colorTarget.canvas, true),
    normal: toTexture(normalTarget.canvas, false),
    roughness: toTexture(roughnessTarget.canvas, false),
  };
}

export function disposeAsphaltTextures(textures: AsphaltTextures | null): void {
  if (textures === null) {
    return;
  }
  textures.color.dispose();
  textures.normal.dispose();
  textures.roughness.dispose();
}
